<template>
  <div>
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">{{ $t('tagManagement.title') }}</h6>
        </div>
    
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <div class="row">
        <div class="col">
          <card>
            <div class="text-right mb-4">
            <base-button 
              type="primary"
              outline
              @click="openCreateDialog"
            >
              <i class="ai ai-plus mr-2"></i>
              {{ $t('tagManagement.createNewTag') }}
            </base-button>
          </div>
            <div v-if="loading" class="text-center py-4">
              <i class="fa fa-spinner fa-spin fa-2x text-primary"></i>
              <p class="mt-2">{{ $t('tagManagement.loadingTags') }}</p>
            </div>

            <div v-else>
              <el-table
                class="table-responsive table-flush"
                header-row-class-name="thead-light"
                :data="paginatedTags"
                :empty-text="$t('tagManagement.noTagsAvailable')"
                border
              >
                <el-table-column label="id" prop="id" width="100" sortable></el-table-column>
                
                <el-table-column :label="$t('general.name')" prop="name" sortable></el-table-column>
                
                <el-table-column :label="$t('tagManagement.type')" width="140" sortable>
                  <template slot-scope="{row}">
                    <span 
                      class="badge"
                      :class="row.assist_admin ? 'badge-warning' : 'badge-success'"
                    >
                      {{ row.assist_admin ? $t('tagManagement.admin') : $t('tagManagement.regular') }}
                    </span>
                  </template>
                </el-table-column>
                <el-table-column 
                  :label="$t('general.actions')" 
                  width="120" 
                  class-name="actions-column"
                >
                  <template slot-scope="{row}">
                    <div class="d-flex">
                        <el-tooltip :content="$t('tagManagement.editTag')" placement="top">
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-primary mr-1"
                            @click="editTag(row)"
                        >
                            <i class="ai ai-edit"></i>
                        </button>
                        </el-tooltip>
                        <el-tooltip :content="$t('tagManagement.deleteTag')" placement="top">
                        <button 
                            type="button" 
                            class="btn btn-sm btn-outline-danger"
                            @click="deleteTag(row)"
                        >
                            <i class="ai ai-remove"></i>
                        </button>
                        </el-tooltip>
                    </div>
                  </template>
                </el-table-column>
              </el-table>

              <el-pagination
                background
                layout="prev, pager, next"
                class="mt-3 text-center"
                :total="itemsCount"
                :page-size="itemsPerPage"
                @current-change="currentPage = $event"
              ></el-pagination>
            </div>
          </card>
        </div>
      </div>
    </div>
    <modal :show.sync="showCreateDialog" body-classes="pt-0" modal-classes="modal-dialog-centered">
      <h4 class="modal-title" slot="header">{{ editingTag ? $t('tagManagement.editTag') : $t('tagManagement.createNewTag') }}</h4>
      
      <el-form :model="tagForm" ref="form" label-width="120px">
        <el-form-item 
          :label="$t('tagManagement.tagName')" 
          prop="name"
          :rules="[{required: true, message: $t('tagManagement.tagNameRequired'), trigger: 'blur'}]"
        >
          <el-input 
            v-model="tagForm.name" 
            :placeholder="$t('tagManagement.enterTagName')"
          ></el-input>
        </el-form-item>

        <el-form-item :label="$t('tagManagement.type')">
          <el-switch
            v-model="tagForm.assist_admin"
            :active-text="$t('tagManagement.adminOnly')"
            :inactive-text="$t('tagManagement.public')"
            active-color="#f39c12"
            inactive-color="#27ae60"
          ></el-switch>
          <small class="form-text text-muted">
            {{ $t('tagManagement.adminOnlyDescription') }}
          </small>
        </el-form-item>

        <el-form-item 
          :label="$t('tagManagement.rank')" 
          prop="rank"
          :rules="[{required: true, message: $t('tagManagement.rankRequired'), trigger: 'blur'}]"
        >
          <el-input-number 
            v-model="tagForm.rank" 
            :min="1"
            :max="editingTag ? maxRank : maxRank + 1"
            placeholder="1"
            class="w-100"
          ></el-input-number>
          <small class="form-text text-muted">
            {{ $t('tagManagement.rankDescription') }}
          </small>
        </el-form-item>
      </el-form>

      <template slot="footer">
        <base-button type="secondary" @click="closeDialog">{{ $t('general.cancel') }}</base-button>
        <base-button 
          type="primary" 
          @click="saveTag"
        >
          <i v-if="saving" class="fa fa-spinner fa-spin mr-2"></i>
          {{ editingTag ? $t('general.update') : $t('general.create') }}
        </base-button>
      </template>
    </modal>

    <modal :show.sync="showDeleteDialog" body-classes="pt-0" modal-classes="modal-dialog-centered">
      <h4 class="modal-title" slot="header">
        <i class="ai ai-remove text-danger mr-2"></i>
        {{ $t('tagManagement.confirmDelete') }}
      </h4>
      
      <div>
        <p>{{ $t('tagManagement.deleteConfirmMessage', { tagName: tagToDelete && tagToDelete.name }) }}</p>
        
        <div v-if="tagToDelete && tagToDelete.hotels_count > 0" class="alert alert-warning">
          <i class="ai ai-warning mr-2"></i>
          {{ $t('tagManagement.tagAssignedWarning', { count: tagToDelete.hotels_count }) }}
          {{ $t('tagManagement.cannotDeleteAssigned') }}
        </div>
      </div>

      <template slot="footer">
        <base-button type="secondary" @click="showDeleteDialog = false">{{ $t('general.cancel') }}</base-button>
        <base-button 
          type="danger" 
          @click="confirmDelete"
          :disabled="tagToDelete && tagToDelete.hotels_count > 0"
        >
          <i v-if="deleting" class="fa fa-spinner fa-spin mr-2"></i>
          {{ $t('general.delete') }}
        </base-button>
      </template>
    </modal>


  </div>
</template>

<script>
import { Modal } from '@/components'
import { 
  Table, 
  TableColumn, 
  Pagination, 
  Tooltip,
  Form,
  FormItem,
  Input,
  InputNumber,
  Switch
} from 'element-ui'

export default {
  name: 'TagManagement',
  components: {
    Modal,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Pagination.name]: Pagination,
    [Tooltip.name]: Tooltip,
    [Form.name]: Form,
    [FormItem.name]: FormItem,
    [Input.name]: Input,
    [InputNumber.name]: InputNumber,
    [Switch.name]: Switch
  },
  data() {
    return {
      loading: false,
      saving: false,
      deleting: false,
      tags: [],
      showCreateDialog: false,
      showDeleteDialog: false,
      editingTag: null,
      tagToDelete: null,
      currentPage: 1,
      itemsPerPage: 10,
      tagForm: {
        name: '',
        assist_admin: false,
        rank: 1
      }
    }
  },
  computed: {
    itemsCount() {
      return this.sortedTags.length
    },
    maxRank() {
      return this.tags.length || 1
    },
    sortedTags() {
      return [...this.tags].sort((a, b) => {
        if (a.rank === null || a.rank === undefined) return 1
        if (b.rank === null || b.rank === undefined) return -1
        
        if (a.rank !== b.rank) {
          return a.rank - b.rank
        }
        
        return a.name.localeCompare(b.name)
      })
    },
    paginatedTags() {
      const from = (this.currentPage - 1) * this.itemsPerPage
      const to = from + this.itemsPerPage
      return this.sortedTags.slice(from, to)
    }
  },
  watch: {
    tags: {
      handler(newTags, oldTags) {
        if (!this.editingTag && this.showCreateDialog && (!oldTags || newTags.length !== oldTags.length)) {
          if (this.tagForm.rank > this.maxRank + 1) {
            this.tagForm.rank = this.maxRank + 1
          }
        }
      },
      immediate: false
    }
  },
  async mounted() {
    await this.loadTags()
  },
  methods: {
    async loadTags() {
      this.loading = true
      try {
        const response = await this.$axios.get('/api/available-tags')
        this.tags = response.data.data
      } catch (error) {
        this.$notify({
          title: this.$t('tagManagement.failedToLoadTags'),
          type: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    editTag(tag) {
      this.editingTag = tag
      this.tagForm = {
        name: tag.name,
        assist_admin: tag.assist_admin,
        rank: tag.rank
      }
      this.showCreateDialog = true
    },

    async saveTag() {
      try {
        await this.$refs.form.validate()
      } catch {
        return
      }

      this.saving = true
      try {
        if (this.editingTag) {
          
          const updateData = {
            name: this.tagForm.name,
            assist_admin: this.tagForm.assist_admin,
            rank: this.tagForm.rank
          }
          
          await this.$axios.put(`/api/available-tags/${this.editingTag.id}`, updateData)
          this.$notify({
            title: this.$t('tagManagement.tagSuccessfullyUpdated')
          })
        } else {
          await this.$axios.post('/api/available-tags', this.tagForm)
          this.$notify({
            title: this.$t('tagManagement.tagSuccessfullyCreated')
          })
        }
        
        await this.loadTags()
        this.closeDialog()
      } catch (error) {
        this.$notify({
          title: (error.response && error.response.data && error.response.data.message) || this.$t('tagManagement.failedToSaveTag'),
          type: 'danger'
        })
      } finally {
        this.saving = false
      }
    },

    deleteTag(tag) {
      this.tagToDelete = tag
      this.showDeleteDialog = true
    },

    async confirmDelete() {
      this.deleting = true
      try {
        await this.$axios.delete(`/api/available-tags/${this.tagToDelete.id}`)
        this.$notify({
          title: this.$t('tagManagement.tagSuccessfullyDeleted')
        })
        await this.loadTags()
        this.showDeleteDialog = false
      } catch (error) {
        this.$notify({
          title: (error.response && error.response.data && error.response.data.message) || this.$t('tagManagement.failedToDeleteTag'),
          type: 'danger'
        })
      } finally {
        this.deleting = false
      }
    },

    closeDialog() {
      this.showCreateDialog = false
      this.editingTag = null
      this.resetForm()
      if (this.$refs.form) {
        this.$refs.form.resetFields()
      }
    },

    resetForm() {
      this.tagForm = {
        name: '',
        assist_admin: false,
        rank: this.maxRank + 1
      }
    },

    openCreateDialog() {
      this.editingTag = null
      this.resetForm()
      this.showCreateDialog = true
    }
  }
}
</script>

<style scoped>
.el-input-number {
  width: 100%;
}

.el-form-item {
  margin-bottom: 18px;
}



.actions-column .btn {
  margin: 0 2px;
}

.form-text {
  line-height: 1.3 !important;
}



</style>
